import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';

interface PageProject {
  name: string;
  imageUrl: string;
  link: string;
  addedAt: string;
  status?: string;
  description?: string;
}

interface CustomPage {
  _id: string;
  pageUrl: string;
  title: string;
  subtitle: string;
  description: string;
  projects: PageProject[];
  isPublished: boolean;
  createdAt: string;
}

interface ServiceProject {
  _id: string;
  title: string;
  slug: string;
  description: string;
  imageUrl: string;
  category: string;
  network: string;
  tags: string[];
  featured: boolean;
}

interface InformationPage {
  _id: string;
  title: string;
  slug: string;
  content: string;
  description: string;
  category: string;
  isPublished: boolean;
  featured: boolean;
  tags: string[];
  imageUrl?: string;
  author: string;
  priority: number;
  status: string;
  createdAt?: string;
}

const CustomPageDisplay: React.FC = () => {
  const { pageUrl } = useParams<{ pageUrl?: string }>();
  const [page, setPage] = useState<CustomPage | null>(null);
  const [informationPage, setInformationPage] = useState<InformationPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allProjects, setAllProjects] = useState<ServiceProject[]>([]);
  const [showServicesModal, setShowServicesModal] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        if (!pageUrl) {
          setError('No page URL provided');
          setLoading(false);
          return;
        }

        console.log('📄 Fetching page:', pageUrl);

        // ❌ СПОЧАТКУ перевіряємо Information Pages
        const informationPages = localStorage.getItem('informationPages');
        if (informationPages) {
          const pages = JSON.parse(informationPages);
          const foundPage = pages.find((p: any) => p.slug === pageUrl && p.isPublished);
          if (foundPage) {
            console.log('✅ Found Information Page');
            setInformationPage(foundPage);
            setLoading(false);
            
            // Завантажуємо проекти для Services modal
            try {
              const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
              const response = await fetch(`${apiUrl}/api/projects?status=active&isPublished=true`);
              const data = await response.json();
              if (data.success) {
                setAllProjects(data.data.projects);
              }
            } catch (error) {
              console.error('Error fetching projects:', error);
            }
            return;
          }
        }

        // ✅ Потім перевіряємо Custom Pages
        const savedPages = localStorage.getItem('stakePages');
        if (savedPages) {
          const customPages: CustomPage[] = JSON.parse(savedPages);
          const formattedPageUrl = pageUrl.startsWith('/') ? pageUrl : '/' + pageUrl;
          
          const foundPage = customPages.find(p => {
            const pagePageUrl = p.pageUrl.startsWith('/') ? p.pageUrl : '/' + p.pageUrl;
            return pagePageUrl === formattedPageUrl && p.isPublished;
          });

          if (foundPage) {
            console.log('✅ Page loaded from localStorage:', foundPage.title);
            setPage(foundPage);
            setLoading(false);
            
            // Завантажуємо проекти для Services modal
            try {
              const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
              const response = await fetch(`${apiUrl}/api/projects?status=active&isPublished=true`);
              const data = await response.json();
              if (data.success) {
                setAllProjects(data.data.projects);
              }
            } catch (error) {
              console.error('Error fetching projects:', error);
            }
            return;
          }
        }

        // Fallback до API
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const response = await axios.get(`${apiUrl}/api/custom-pages/by-url/${pageUrl}`, {
          withCredentials: true
        });

        console.log('✅ Page loaded from API:', response.data);

        if (response.data.success && response.data.data) {
          setPage(response.data.data);
          
          // Завантажуємо проекти для Services modal
          try {
            const projectsResponse = await fetch(`${apiUrl}/api/projects?status=active&isPublished=true`);
            const projectsData = await projectsResponse.json();
            if (projectsData.success) {
              setAllProjects(projectsData.data.projects);
            }
          } catch (error) {
            console.error('Error fetching projects:', error);
          }
        } else {
          setError('Page not found');
        }
      } catch (err: any) {
        console.error('❌ Error loading page:', err);
        if (err.response?.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [pageUrl]);

  if (loading) {
    return (
      <Layout currentPage="other" showServicesModal={showServicesModal} setShowServicesModal={setShowServicesModal} projects={allProjects} headerVariant="main">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 mb-4">
              <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-white text-lg">Loading page...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || (!page && !informationPage)) {
    return (
      <Navigate to="/" replace />
    );
  }

  // ✅ Якщо це Information Page - рендеримо його
  if (informationPage) {
    return (
      <Layout currentPage="other" showServicesModal={showServicesModal} setShowServicesModal={setShowServicesModal} projects={allProjects} headerVariant="main">
        <article className="max-w-4xl mx-auto px-6 py-12">
          {/* Title Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {informationPage?.title}
            </h1>
            {informationPage && informationPage.description && (
              <p className="text-lg text-gray-300">
                {informationPage.description}
              </p>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-700 my-8"></div>

          {/* Content */}
          {informationPage && (
            <>
              <style>{`
                .info-page-content { white-space: normal; word-wrap: break-word; }
                .info-page-content h1 { font-size: 2rem; font-weight: bold; color: white; margin-bottom: 1.5rem; margin-top: 2rem; }
                .info-page-content h1:first-child { margin-top: 0; }
                .info-page-content h2 { font-size: 1.875rem; font-weight: bold; color: white; margin-bottom: 1rem; margin-top: 1.5rem; }
                .info-page-content h3 { font-size: 1.5rem; font-weight: bold; color: white; margin-bottom: 0.75rem; margin-top: 1rem; }
                .info-page-content h4 { font-size: 1.25rem; font-weight: bold; color: white; margin-bottom: 0.5rem; margin-top: 0.75rem; }
                .info-page-content p { margin-bottom: 1.25rem; color: #d1d5db; line-height: 1.8; }
                .info-page-content a { color: #60a5fa; text-decoration: none; }
                .info-page-content a:hover { color: #93c5fd; text-decoration: underline; }
                .info-page-content strong { color: white; font-weight: bold; }
                .info-page-content em { font-style: italic; color: #d1d5db; }
                .info-page-content code { background-color: #1f2937; color: #d1d5db; padding: 0.25rem 0.5rem; border-radius: 0.25rem; font-size: 0.875rem; font-family: monospace; }
                .info-page-content pre { background-color: #1f2937; color: #d1d5db; padding: 1rem; border-radius: 0.375rem; overflow-x: auto; margin-bottom: 1.5rem; margin-top: 1rem; white-space: pre-wrap; word-wrap: break-word; }
                .info-page-content pre code { background-color: transparent; padding: 0; color: inherit; }
                .info-page-content blockquote { border-left: 4px solid #60a5fa; padding-left: 1rem; color: #9ca3af; font-style: italic; margin-bottom: 1.5rem; margin-top: 1rem; margin-right: 0; }
                .info-page-content ul { list-style-type: disc; padding-left: 2rem; margin-bottom: 1.25rem; margin-top: 0.5rem; }
                .info-page-content ol { list-style-type: decimal; padding-left: 2rem; margin-bottom: 1.25rem; margin-top: 0.5rem; }
                .info-page-content li { margin-bottom: 0.75rem; color: #d1d5db; }
                .info-page-content table { width: 100%; border-collapse: collapse; margin-bottom: 1.5rem; margin-top: 1rem; }
                .info-page-content th { background-color: #374151; color: white; padding: 0.75rem; text-align: left; font-weight: bold; border: 1px solid #4b5563; }
                .info-page-content td { border: 1px solid #4b5563; padding: 0.75rem; color: #d1d5db; }
                .info-page-content hr { border: none; border-top: 1px solid #4b5563; margin: 2rem 0; }
              `}</style>
              <div
                className="info-page-content"
                dangerouslySetInnerHTML={{ __html: informationPage.content }}
              />
            </>
          )}
        </article>
      </Layout>
    );
  }

  // ✅ Якщо це Custom Page - рендеримо його
  return (
    <Layout currentPage="other" showServicesModal={showServicesModal} setShowServicesModal={setShowServicesModal} projects={allProjects} headerVariant="main">
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {page!.title}
          </h1>
          {page!.subtitle && (
            <p className="text-lg md:text-xl text-gray-300 mb-3 max-w-4xl mx-auto">
              {page!.subtitle}
            </p>
          )}
          {page!.description && (
            <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto">
              {page!.description}
            </p>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        {page!.projects && page!.projects.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-6">
            {page!.projects.map((project: PageProject, index: number) => {
              const projectLink = project.link || '#';
              const isExternalLink = projectLink.startsWith('http');

              const ProjectBlock = () => (
                <div className="dashboard-block p-7 text-center w-[180px] h-[180px] min-w-[180px] min-h-[180px] max-w-[180px] max-h-[180px] flex flex-col justify-between hover:scale-105 transition-transform">
                  {/* Logo/Icon */}
                  <div className="flex justify-center mb-2">
                    {project.imageUrl ? (
                      <img 
                        src={getFullImageUrl(project.imageUrl)} 
                        alt={project.name}
                        className="w-16 h-16 rounded-xl object-cover"
                        onError={handleImageError}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                        {project.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  
                  {/* Project Name */}
                  <h3 className="text-white font-semibold text-sm mb-3 group-hover:text-indigo-500 transition-colors uppercase">
                    {project.name}
                  </h3>
                </div>
              );

              return isExternalLink ? (
                <a
                  key={project.addedAt || `project-${index}`}
                  href={projectLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <ProjectBlock />
                </a>
              ) : (
                <div key={project.addedAt || `project-${index}`} className="group block cursor-pointer">
                  <ProjectBlock />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400">No projects available on this page.</p>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default CustomPageDisplay;
